import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { motion } from 'motion/react';
import { Link } from 'react-router';
import { TASK_CYCLE_DURATION, TASK_BURST_EASE } from '../../TaskConnectionWidget';

const travel = keyframes`
  to { stroke-dashoffset: -54; }
`;

const buttonSheen = keyframes`
  0%, 8% { opacity: 0; transform: translateX(0) skewX(-22deg); }
  14% { opacity: 1; }
  42% { opacity: 1; }
  50%, 100% { opacity: 0; transform: translateX(360%) skewX(-22deg); }
`;

const triggerTravel = keyframes`
  0% { stroke-dashoffset: 1; opacity: 0; }
  3% { opacity: 1; }
  25% { stroke-dashoffset: 0; opacity: 1; }
  28%, 100% { stroke-dashoffset: 0; opacity: 0; }
`;

const taskExecution = keyframes`
  0%, 25% { transform: rotate(0deg); }
  70%, 100% { transform: rotate(360deg); }
`;

export const platformStoryBanner = require('../../../../media/banner.png');

export namespace S {
  export const Backdrop = styled.div`
    background-image: url(${platformStoryBanner});
    background-position: center -410px;
    background-repeat: no-repeat;
    background-size: 100%;
  `;
  export const HeroFrame = styled.div`
    min-height: calc(100dvh - ${(p) => p.theme.header.height}px);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-block: clamp(28px, 4dvh, 56px);
    > div {
      width: 100%;
    }
  `;

  export const Eyebrow = styled.div`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.17em;
    color: ${(p) => p.theme.text.special};
  `;
  export const Intro = styled.div`
    min-height: clamp(540px, 65vh, 760px);
    box-sizing: border-box;
    padding: 80px 28px 46px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    border: 1px solid ${(p) => p.theme.surface.border};
    border-radius: 24px;
    background:
      radial-gradient(ellipse at 50% 40%, ${(p) => p.theme.featureGraph.interface.background}, transparent 65%),
      radial-gradient(${(p) => p.theme.surface.border} 1px, transparent 1px), ${(p) => p.theme.surface.background};
    background-size:
      auto,
      32px 32px,
      auto;
    h1 {
      font-size: clamp(38px, 5.3vw, 76px);
      line-height: 1.06;
      letter-spacing: 0;
      font-weight: 650;
      color: ${(p) => p.theme.text.heading};
      margin: 28px 0 24px;
      em {
        font-style: normal;
        color: ${(p) => p.theme.text.special};
      }
    }
    p {
      max-width: 690px;
      margin: 0;
      color: ${(p) => p.theme.text.paragraph};
      font-size: clamp(14px, 1.5vw, 18px);
      line-height: 1.7;
    }
    @media (max-width: 600px) {
      padding: 60px 20px 32px;
    }
  `;
  export const StoryIntro = styled(motion.create(Intro))`
    position: absolute;
    inset: 0;
    z-index: 1;
    min-height: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    padding: 24px;
    h1 {
      font-size: clamp(34px, 4.3vw, 62px);
      margin: 18px 0 16px;
    }
    p {
      font-size: clamp(13px, 1.2vw, 16px);
    }
    button {
      margin-top: 24px;
    }
    @media (max-width: 600px) {
      padding: 24px 18px;
    }
  `;
  export const SceneViewport = styled.div`
    position: relative;
  `;
  export const IntroCustomers = styled.div`
    width: 100%;
    max-width: 1160px;
    margin-top: clamp(56px, 8vh, 90px);
    > span {
      display: block;
      margin-top: 24px;
      color: ${(p) => p.theme.text.paragraph};
      font-size: 11px;
      a {
        color: ${(p) => p.theme.text.special};
        text-decoration: none;
        &:hover {
          text-decoration: underline;
        }
      }
    }
    > div {
      padding: 0;
      gap: 36px;
      img {
        max-width: min(16vw, 200px);
        max-height: 64px;
      }
    }
    @media (max-width: 600px) {
      > div {
        gap: 16px;
        img {
          max-width: 26vw;
          max-height: 48px;
        }
      }
    }
  `;
  export const HomeContent = styled.div`
    max-width: 1300px;
    margin: 20px auto 0;
  `;
  export const StartButton = styled.button`
    position: relative;
    isolation: isolate;
    overflow: hidden;
    display: inline-flex;
    align-items: center;
    gap: 20px;
    margin-top: 34px;
    padding: 17px 25px;
    border: 1px solid ${(p) => p.theme.home.walkthroughButton.border};
    border-radius: 10px;
    background: ${(p) => p.theme.home.walkthroughButton.background};
    color: ${(p) => p.theme.text.heading};
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    &::after {
      content: '';
      position: absolute;
      z-index: -1;
      inset: -75% auto -75% -60%;
      width: 50%;
      opacity: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        color-mix(in srgb, ${(p) => p.theme.home.walkthroughButton.border} 8%, transparent) 22%,
        color-mix(in srgb, ${(p) => p.theme.home.walkthroughButton.border} 22%, transparent) 42%,
        color-mix(in srgb, ${(p) => p.theme.home.walkthroughButton.icon} 42%, transparent) 49%,
        color-mix(in srgb, ${(p) => p.theme.home.walkthroughButton.border} 12%, transparent) 54%,
        color-mix(in srgb, ${(p) => p.theme.home.walkthroughButton.icon} 20%, transparent) 61%,
        color-mix(in srgb, ${(p) => p.theme.home.walkthroughButton.border} 6%, transparent) 76%,
        transparent 100%
      );
      animation: ${buttonSheen} 4.8s cubic-bezier(0.45, 0, 0.2, 1) infinite;
      pointer-events: none;
    }
    svg {
      color: ${(p) => p.theme.home.walkthroughButton.icon};
      position: relative;
    }
    &:hover {
      background: ${(p) => p.theme.home.walkthroughButton.hoverBackground};
    }
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.home.walkthroughButton.focus};
      outline-offset: 5px;
    }
    @media (prefers-reduced-motion: reduce) {
      &::after {
        animation: none;
      }
    }
  `;

  export const Theater = styled.div`
    border: 1px solid ${(p) => p.theme.surface.border};
    background: ${(p) => p.theme.surface.background};
    border-radius: 24px;
    overflow: hidden;
    transition:
      background-color 900ms ease,
      border-color 900ms ease,
      box-shadow 900ms ease;
    &[data-intro='true'],
    &[data-finale='true'] {
      background: transparent;
      border-color: transparent;
      box-shadow: none;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
    box-shadow: ${(p) => p.theme.surface.shadow};
  `;
  export const Caption = styled.div`
    display: flex;
    gap: 16px;
    padding: 30px 34px 0;
    min-height: 126px;
    box-sizing: border-box;
    h2,
    h3 {
      font-size: clamp(19px, 2vw, 27px);
      line-height: 1.2;
      margin: 0 0 10px;
      font-weight: 600;
      letter-spacing: -0.025em;
      color: ${(p) => p.theme.text.heading};
    }
    p {
      color: ${(p) => p.theme.text.paragraph};
      font-size: 14px;
      line-height: 1.6;
      max-width: 730px;
      margin: 0;
    }
    @media (max-width: 600px) {
      padding: 24px 20px 0;
      gap: 14px;
      min-height: 172px;
      p {
        font-size: 13px;
      }
    }
  `;
  export const Stage = styled.div`
    transition: opacity 900ms ease;
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
    height: clamp(400px, 46vw, 640px);
    @media (min-width: 1101px) {
      [data-landing='true'] & {
        height: clamp(400px, calc(100dvh - ${(p) => p.theme.header.height}px - 310px), 640px);
      }
    }
    position: relative;
    background: radial-gradient(
      ellipse at 45% 55%,
      ${(p) => p.theme.featureGraph.cloudService.background},
      transparent 70%
    );
    @media (max-width: 600px) {
      height: 390px;
    }
  `;
  export const ExploreLink = StartButton.withComponent(Link);
  export const FinaleMessage = styled.div`
    position: absolute;
    inset: 0 0 auto;
    height: 126px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 8px 24px;
    @media (max-width: 600px) {
      height: 172px;
      padding-inline: 20px;
    }
    text-align: center;
    h2,
    h3 {
      font-size: clamp(26px, 3vw, 40px);
      line-height: 1.08;
      letter-spacing: -0.035em;
      font-weight: 600;
      color: ${(p) => p.theme.text.heading};
      margin: 8px 0;
      text-wrap: balance;
    }
    p {
      max-width: 760px;
      margin: 0 auto;
      color: ${(p) => p.theme.text.paragraph};
      font-size: clamp(13px, 1.2vw, 16px);
      line-height: 1.5;
      text-wrap: balance;
    }
  `;
  export const FinaleAction = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: min(100%, 440px);
    height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    background: radial-gradient(
      ellipse at center,
      ${(p) => p.theme.surface.background} 35%,
      color-mix(in srgb, ${(p) => p.theme.surface.background} 92%, transparent) 52%,
      transparent 78%
    );
    a {
      pointer-events: auto;
      text-decoration: none;
      margin-top: 0;
      &::after {
        animation: none;
      }
    }
  `;
  export const World = styled(motion.svg)`
    display: block;
    width: 100%;
    height: 100%;
    font-family: 'Inter', sans-serif;
  `;
  export const Flow = styled.path<{ running: boolean }>`
    animation: ${travel} 2s linear infinite;
    animation-play-state: ${(p) => (p.running ? 'running' : 'paused')};
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `;
  export const TriggerPulse = styled.path<{ active: boolean; running: boolean }>`
    opacity: 0;
    animation: ${triggerTravel} ${TASK_CYCLE_DURATION} linear infinite;
    animation-name: ${(p) => (p.active ? triggerTravel : 'none')};
    animation-play-state: ${(p) => (p.running ? 'running' : 'paused')};
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `;
  export const TaskOrbit = styled.g<{ active: boolean; running: boolean }>`
    animation: ${taskExecution} ${TASK_CYCLE_DURATION} ${TASK_BURST_EASE} infinite;
    animation-name: ${(p) => (p.active ? taskExecution : 'none')};
    animation-play-state: ${(p) => (p.running ? 'running' : 'paused')};
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `;
  export const Controls = styled.div`
    transition: border-color 900ms ease;
    [data-intro='true'] &,
    [data-finale='true'] & {
      border-top-color: transparent;
    }
    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
    display: flex;
    align-items: center;
    gap: 30px;
    padding: 22px 32px 26px;
    @media (max-width: 600px) {
      padding: 18px;
      gap: 16px;
      align-items: flex-end;
    }
  `;
  export const Chapters = styled.div<{ count: number }>`
    display: grid;
    grid-template-columns: repeat(${(p) => p.count}, minmax(0, 1fr));
    flex: 1;
    gap: 16px;
    @media (max-width: 1100px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
    @media (max-width: 700px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 16px;
    }
  `;
  export const Chapter = styled.button`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    border: 0;
    padding: 0;
    background: none;
    text-align: left;
    color: ${(p) => p.theme.text.paragraph};
    font: inherit;
    font-size: 12px;
    line-height: 1.5;
    cursor: pointer;
    small {
      flex-shrink: 0;
      opacity: 0.5;
      font-variant-numeric: tabular-nums;
    }
    &[aria-current],
    &:hover {
      color: ${(p) => p.theme.text.heading};
    }
    &[aria-current] small {
      opacity: 1;
      color: ${(p) => p.theme.text.special};
    }
    &:focus-visible {
      outline: 2px solid ${(p) => p.theme.text.special};
      outline-offset: 6px;
    }
  `;
  export const ChapterLabel = styled.span`
    display: flex;
    align-items: baseline;
    gap: 8px;
    > span {
      text-wrap: balance;
    }
  `;
  export const Progress = styled.div`
    &[data-selected='true'] span {
      background: #22d3ee;
    }
    flex-shrink: 0;
    width: 100%;
    height: 2px;
    border-radius: 2px;
    margin-bottom: 12px;
    background: ${(p) => p.theme.surface.border};
    overflow: hidden;
    span {
      display: block;
      height: 100%;
      background: ${(p) => p.theme.text.special};
      width: 100%;
      transform-origin: left center;
      will-change: transform;
    }
    @media (prefers-reduced-motion: reduce) {
      span {
        transition: none;
      }
    }
  `;
  export const Playback = styled.div`
    display: flex;
    gap: 6px;
    button {
      width: 36px;
      height: 36px;
      border: 1px solid ${(p) => p.theme.surface.border};
      border-radius: 50%;
      background: transparent;
      color: ${(p) => p.theme.text.paragraph};
      cursor: pointer;
      &:hover {
        color: ${(p) => p.theme.text.special};
        background: ${(p) => p.theme.card.hoverBackground};
      }
      &:focus-visible {
        outline: 2px solid ${(p) => p.theme.text.special};
        outline-offset: 3px;
      }
    }
    @media (max-width: 600px) {
      flex-direction: column;
    }
  `;
}
